import type { Event, ScheduleCard } from '@pa/core';
import type { ChatMessage } from './agent';

/** The schedule cards persisted on TWO's bubble, when any. */
export function cardsOf(ev: Event): ScheduleCard[] | undefined {
  const raw = ev.raw_payload as { cards?: unknown } | undefined;
  const cards = raw?.cards;
  return Array.isArray(cards) && cards.length ? (cards as ScheduleCard[]) : undefined;
}

/**
 * One event → one message (M5.2). Used by the history path and the socket-frame path alike,
 * so a message's time is always the server's `created_at`, never the client's receipt time.
 */
export function messageFromEvent(ev: Event): ChatMessage {
  return {
    id: ev.event_id,
    role: ev.direction === 'inbound' ? 'user' : 'agent',
    text: ev.text ?? '',
    at: ev.created_at,
    reactions: [],
    attachments: ev.attachments,
    trace_id: ev.trace_id,
    ...(cardsOf(ev) ? { cards: cardsOf(ev) } : {}),
  };
}

/** True when the event carries a message the list shows. */
export function isContentful(ev: Event): boolean {
  return (
    ['text', 'audio', 'image', 'document'].includes(ev.type) &&
    Boolean(ev.text || ev.attachments.length)
  );
}

/** History page → messages, reactions attached to their targets. */
export function eventsToMessages(events: readonly Event[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  const order: string[] = [];
  for (const ev of events) {
    if (isContentful(ev)) {
      byId.set(ev.event_id, messageFromEvent(ev));
      order.push(ev.event_id);
    }
  }
  for (const ev of events) {
    if (ev.type === 'reaction' && ev.reaction && ev.reply_to) {
      const target = byId.get(ev.reply_to);
      if (target && !target.reactions.includes(ev.reaction)) target.reactions.push(ev.reaction);
    }
  }
  // 👀 meant "reading"; once the reply exists it has been answered (👍 stays).
  for (const ev of events) {
    if (ev.direction === 'outbound' && isContentful(ev) && ev.reply_to) {
      const target = byId.get(ev.reply_to);
      if (target) target.reactions = target.reactions.filter((r) => r !== '👀');
    }
  }
  return order.flatMap((id) => {
    const m = byId.get(id);
    return m ? [m] : [];
  });
}
